import { AutoDimensionType } from "building-types/autoDimensions/autoDimensionTypesEnum";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import type { EntitySet } from "building-types/entities/entitySetTypes";
import {
  isAutoDimensionHandle,
  unpackAutoDimensionFloorPlanExteriorMultipleHandle,
  unpackAutoDimensionFloorPlanInteriorByRoomEdgePairHandle,
  unpackAutoDimensionFloorPlanInteriorByOrthogonalRoomEdgeHandle,
  unpackAutoDimensionFoundationExteriorMultipleHandle,
} from "building-types/handles/autoDimensionHandleTools";
import type { AutoDimensionHandle } from "building-types/handles/docEntityHandleTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import {
  builtinViewablePlaceholder,
  BuiltinViewablePlaceholderId,
  isViewSelectable,
  SelectorTypeToPlanGeomDocEntityHandleType,
  SelectorTypeToSheetGeometryCategories,
  viewOverridableHandle,
} from "building-types/selectors/selectorTools";
import type {
  AutoDimensionSelector,
  Selector,
  SelectorType,
  ViewOverridableHandle,
  ViewOverride,
} from "building-types/selectors/selectorTypes";
import type {
  ViewSelectableAndUnselectable,
  ViewSelector,
  ViewSelectable,
} from "building-types/selectors/viewSelectorTypes";
import type { SheetGeometryCategoryTable, SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import type { RWTable, Table } from "common/data/table";
import { docEntityHandleHash } from "doc/entity/core/handleHash";
import { isViewOverrideHandle } from "doc/entity/docEntityHandleTools";
import { docEntityTableFilter } from "doc/entity/docEntityTableFilter";
import type { SheetGeometryFilter, PlanGeometrySource } from "doc/plans/planGeometrySource";
import { SheetGeometrySourceFilter } from "doc/plans/planGeometrySource";
import type { Categorized3DGeometry, SheetGeometrySelector } from "doc/plans/planGeometrySourceTypes";

/**
 *
 * @param views This is purposefully broad to allow for wide usage. Viewables that are not valid for the current context will be filtered out.
 * @param viewOverrides
 * @param viewSelectors
 * @param selectors
 * @param selected Allow selected entities (Editor state) that would otherwise be suppressed to be visible, so we can highlight them.
 * This property should not be set where it would affect pdfs or other exports, since it would show entities that are supposed to be hidden.
 * But it is useful for user interaction and understanding of the view overrides' domain.
 */
export function applyViewOverrideToSheetGeometrySourceFilter(
  views: ViewSelectableAndUnselectable[],
  viewOverrides: Table<ViewOverride>,
  viewSelectors: Table<ViewSelector>,
  selectors: Table<Selector>,
  selected: EntitySet = {},
): SheetGeometrySourceFilter {
  return new SheetGeometrySourceFilter(
    makeEntitySuppressionFilter(
      applyViewOverrides(views.filter(isViewSelectable), viewOverrides, viewSelectors, selectors, selected),
    ),
    {},
  );
}

/**
 *
 * @param views This is purposefully broad to allow for wide usage. Viewables that are not valid for the current context will be filtered out.
 * @param viewOverrides
 * @param viewSelectors
 * @param selectors
 * @returns
 */
export function applyViewOverrideToSheetGeometryCategoryTableFilter(
  views: ViewSelectable[],
  viewOverrides: Table<ViewOverride>,
  viewSelectors: Table<ViewSelector>,
  selectors: Table<Selector>,
): SheetGeometryCategoryTable<SheetGeometrySelector> {
  const { suppressedDocEntityHashes, categoriesToSuppress, handleTypesToSuppress, selectedDocEntityHashes } =
    applyViewOverrides(views, viewOverrides, viewSelectors, selectors);

  return categoriesToSuppress.reduce((acc, category) => {
    acc[category] = (g) =>
      filterSheetGeometryByTypeAndHash(g, handleTypesToSuppress, suppressedDocEntityHashes, selectedDocEntityHashes);
    return acc;
  }, {} as SheetGeometryCategoryTable<SheetGeometrySelector>);
}

export function applyViewOverrideToSheetGeometryList(
  viewOverrides: Table<ViewOverride>,
  viewSelectors: Table<ViewSelector>,
  selectors: Table<AutoDimensionSelector>,
  geometry: SheetGeometry[],
): SheetGeometry[] {
  const { suppressedDocEntityHashes, categoriesToSuppress, handleTypesToSuppress, selectedDocEntityHashes } =
    applyViewOverrides([builtinViewablePlaceholder], viewOverrides, viewSelectors, selectors);

  if (categoriesToSuppress.length && Object.keys(suppressedDocEntityHashes).length) {
    geometry = geometry.filter((g) =>
      filterSheetGeometryByTypeAndHash(g, handleTypesToSuppress, suppressedDocEntityHashes, selectedDocEntityHashes),
    );
  }
  return geometry;
}

function makeEntitySuppressionFilter({
  suppressedDocEntityHashes,
  categoriesToSuppress,
  handleTypesToSuppress,
  selectedDocEntityHashes,
}: CategorySuppression): SheetGeometryCategoryTable<SheetGeometryFilter> {
  return categoriesToSuppress.reduce((filter, category) => {
    filter[category] = async (source: PlanGeometrySource, tag: SheetGeometryCategory) => {
      const result: SheetGeometry[] = [];
      (await source.getPlanGeometry(tag)).forEach((g) => {
        if (
          filterSheetGeometryByTypeAndHash(g, handleTypesToSuppress, suppressedDocEntityHashes, selectedDocEntityHashes)
        ) {
          result.push(g);
        }
      });
      return result;
    };
    return filter;
  }, {});
}

/**
 * Returns true if the geometry should be included.
 */
function filterSheetGeometryByTypeAndHash(
  g: SheetGeometry | Categorized3DGeometry,
  handleTypesToSuppress: { [key in DocEntityType]?: boolean },
  suppressedDocEntityHashes: { [hash: string]: boolean },
  selectedDocEntityHashes: { [hash: string]: boolean } = {},
) {
  // if not handle type then just include it
  if (!g?.handle?.type) {
    return true;
  }

  // if the handle type is not in the list of handle types to suppress, then keep it
  if (!handleTypesToSuppress[g.handle.type]) {
    return true;
  }

  if (isAutoDimensionHandle(g.handle)) {
    let handles: AutoDimensionHandle[] = [];
    const provenance = g?.handle?.provenance;
    switch (provenance.type) {
      case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_EXTERIOR_BY_ROOM_MULTIPLE: {
        handles = unpackAutoDimensionFloorPlanExteriorMultipleHandle(provenance);
        break;
      }
      case AutoDimensionType.AUTO_DIMENSION_FOUNDATION_EXTERIOR_BY_ROOM_MULTIPLE: {
        handles = unpackAutoDimensionFoundationExteriorMultipleHandle(provenance);
        break;
      }
      case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_INTERIOR_BY_OPPOSITE_ROOM_EDGE_PAIR: {
        handles = unpackAutoDimensionFloorPlanInteriorByRoomEdgePairHandle(provenance);
        break;
      }
      case AutoDimensionType.AUTO_DIMENSION_FLOOR_PLAN_INTERIOR_BY_ORTHOGONAL_ROOM_EDGE: {
        handles = unpackAutoDimensionFloorPlanInteriorByOrthogonalRoomEdgeHandle(provenance);
        break;
      }
      default:
    }

    if (handles.length > 0) {
      /**
       * if any are suppressed then suppress the whole thing, but...
       * if any are selected, then keep the whole thing for entity highlighting and discovery
       */
      let show = true;
      for (const handle of handles) {
        if (selectedDocEntityHashes[docEntityHandleHash(handle)]) {
          return true;
        }
        if (suppressedDocEntityHashes[docEntityHandleHash(handle)]) {
          show = false;
        }
      }
      return show;
    }
  }

  if (selectedDocEntityHashes[docEntityHandleHash(g.handle)]) {
    return true;
  }

  // okay, we have the type we want to suppress, so check if the hash is in the list of suppressed hashes
  return !suppressedDocEntityHashes[docEntityHandleHash(g.handle)];
}

/**
 * These are just hierarchically organized data structures that are used to filter out certain types of geometry as performantly as possible.
 * 1. Start with the categories in the SheetGeometry (which aren't there for built-in types),
 * 2. Then filter by the handle type
 * 3. Finally filter by the docEntity hash.
 */
interface CategorySuppression {
  readonly categoriesToSuppress: SheetGeometryCategory[];
  readonly suppressedDocEntityHashes: { [hash: string]: boolean };
  readonly selectedDocEntityHashes: { [hash: string]: boolean };
  readonly handleTypesToSuppress: { [key in DocEntityType]?: true }; // allows ignoring plan geoms based on handle without calculating handle hashes for suppression
}

function applyViewOverrides(
  viewables: ViewSelectable[],
  viewOverrideTable: Table<ViewOverride>,
  viewSelectorTable: Table<ViewSelector>,
  selectorTable: Table<Selector>,
  selected: EntitySet = {},
): CategorySuppression {
  if (!viewables || viewables.length === 0) {
    return {
      suppressedDocEntityHashes: {},
      categoriesToSuppress: [],
      handleTypesToSuppress: {},
      selectedDocEntityHashes: {},
    };
  }

  const viewOverridesForView = getViewOverrideIdsForViews(viewables, viewSelectorTable);
  if (Object.keys(viewOverridesForView).length === 0) {
    return {
      suppressedDocEntityHashes: {},
      categoriesToSuppress: [],
      handleTypesToSuppress: {},
      selectedDocEntityHashes: {},
    };
  }

  return determineSuppressedGeometry(viewOverrideTable, viewOverridesForView, selectorTable, selected);
}

function getViewOverrideIdsForViews(viewables: ViewSelectable[], viewSelectorTable: Table<ViewSelector>) {
  const viewableIds = viewables.reduce(
    (acc, v) => {
      const id = v.id;
      if (id) {
        acc[id] = true;
      }
      return acc;
    },
    {} as { [id: string]: boolean },
  );

  const viewOverridesForView = Object.values(viewSelectorTable).reduce(
    (acc, x) => {
      if (x.viewSelectorType === "DOC_VIEW") {
        if (viewableIds[x.viewId]) {
          acc[x.overrideId] = true;
        }
      } else if (x.viewSelectorType === "BUILT_IN_VIEW") {
        if (viewableIds[BuiltinViewablePlaceholderId]) {
          acc[x.overrideId] = true;
        }
      }

      return acc;
    },
    {} as { [id: string]: boolean },
  );
  return viewOverridesForView;
}

function determineSuppressedGeometry(
  viewOverrideTable: Table<ViewOverride>,
  viewOverridesForView: { [id: string]: boolean },
  selectorTable: Table<Selector>,
  selected: EntitySet = {},
): CategorySuppression {
  const selectedHashes = gatherSelectedSelectorsDirectlyAndByViewOverride(selected, selectorTable);

  const { newTable: viewOverrides } = docEntityTableFilter(
    viewOverrideTable,
    (x) => viewOverridesForView[x.id] && x.action === "HIDE",
  );

  const selectorTypes: { [key in SelectorType]?: boolean } = {};
  const suppressedDocEntityHashes: { [hash: string]: boolean } = {};
  for (const selectorId in selectorTable) {
    const selector = selectorTable[selectorId];
    if (!selector?.overrideId) {
      continue;
    }

    selectorTypes[selector.selectorType] = true;

    const viewOverride = viewOverrides[selector.overrideId];
    if (!viewOverride) {
      continue;
    }

    if (!selector.entityHandle) {
      continue;
    }
    const suppressionHash = docEntityHandleHash(selector.entityHandle as DocEntityHandle);
    suppressedDocEntityHashes[suppressionHash] = true;
  }

  // These could become less efficient if more heterogeneous types are added to the selector types
  // The next version could be to have a map of the selector type to the categories and handle types to suppress
  const categoriesToSuppress: { [key in SheetGeometryCategory]?: true } = {};
  const handleTypesToSuppress: { [key in DocEntityType]?: true } = {};
  for (const selectorType in selectorTypes) {
    const selectorCategories = SelectorTypeToSheetGeometryCategories[selectorType];
    if (selectorCategories) {
      for (const category in selectorCategories) {
        categoriesToSuppress[category] = true;
      }
    }
    const planGeomDocEntityHandleTypes = SelectorTypeToPlanGeomDocEntityHandleType[selectorType];
    if (planGeomDocEntityHandleTypes) {
      for (const type in planGeomDocEntityHandleTypes) {
        handleTypesToSuppress[type] = true;
      }
    }
  }

  return {
    suppressedDocEntityHashes,
    categoriesToSuppress: Object.keys(categoriesToSuppress) as SheetGeometryCategory[],
    handleTypesToSuppress,
    selectedDocEntityHashes: selectedHashes,
  };
}

function gatherSelectedSelectorsDirectlyAndByViewOverride(
  selected: EntitySet,
  selectorTable: Table<Selector>,
): { [hash: string]: boolean } {
  if (!selected || !selectorTable) {
    return {};
  }

  const selectedViewOverrideIds = Object.values(selected)
    .filter(isViewOverrideHandle)
    .reduce(
      (acc, x) => {
        acc[x.id] = true;
        return acc;
      },
      {} as { [id: string]: boolean },
    );

  const viewOverridableEntityHandles = Object.values(selectorTable)
    .filter((x) => selectedViewOverrideIds[x.overrideId])
    .map((x) => x.entityHandle) as ViewOverridableHandle[];
  // add selectors that are directly selected
  viewOverridableEntityHandles.push(...Object.values(selected).filter(viewOverridableHandle));

  // make an EntitySet of of the selectors
  const selectedSelectors = viewOverridableEntityHandles.reduce((acc, x) => {
    acc[docEntityHandleHash(x as DocEntityHandle)] = true;
    return acc;
  }, {}) as { [hash: string]: boolean };

  return selectedSelectors;
}

export function getAvailableViewOverrides(
  viewOverrideIds: Table<true>,
  viewOverrideIdToCurrentHandleHashes: Table<Table<boolean>>,
  handles: ViewOverridableHandle[],
) {
  const availableViewOverrideIds: RWTable<true> = {};
  for (const viewOverrideId in viewOverrideIds) {
    const currentHandleHashes = viewOverrideIdToCurrentHandleHashes[viewOverrideId];
    if (!currentHandleHashes) {
      // if there are no selectors pointing to this view override, then the view override is available
      // this shouldn't occur, since removing all selectors should remove the view override
      availableViewOverrideIds[viewOverrideId] = true;
      continue;
    }

    if (handles.filter((handle) => !currentHandleHashes[docEntityHandleHash(handle as DocEntityHandle)]).length) {
      // if there are handles that are not in the view override, then the view override is available
      availableViewOverrideIds[viewOverrideId] = true;
    }
  }
  return availableViewOverrideIds;
}
/**
 * Only return handles that aren't already in selectors.
 */
export function filterHandlesBySelectors(
  viewOverrideId: string,
  handles: ViewOverridableHandle[],
  viewOverrideIdToCurrentHandleHashes: RWTable<RWTable<boolean>>,
): ViewOverridableHandle[] {
  const currentHandleHashes = viewOverrideIdToCurrentHandleHashes[viewOverrideId];
  if (!currentHandleHashes) {
    return handles;
  }
  return handles.filter((handle) => !currentHandleHashes[docEntityHandleHash(handle as DocEntityHandle)]);
}
